import React from "react";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

export type DesignModalShellProps = {
  open: boolean;
  onClose: () => void;
  /** Disables the close button while an action runs. */
  busy?: boolean;
  /** Fill of the 64px icon circle. */
  circleColor: string;
  /** Glyph inside the circle; SVGs are capped at 58% of the circle. */
  icon: React.ReactNode;
  labelledBy?: string;
  describedBy?: string;
  ariaLabel?: string;
  zIndex?: number;
  children?: React.ReactNode;
};

// The Claude Design pop-up card shared by ActionSuccessModal and the standard
// confirm: 440px, 14px radius, red round close, 64px circle. It is built on
// MUI Dialog so it registers with MUI's modal manager and nests safely inside
// other MUI modals (a separate focus trap would fight theirs).
export const DesignModalShell = ({
  open,
  onClose,
  busy = false,
  circleColor,
  icon,
  labelledBy,
  describedBy,
  ariaLabel,
  zIndex,
  children,
}: DesignModalShellProps) => {
  const handleClose = () => {
    if (!busy) onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      sx={{
        zIndex: zIndex ?? 10000,
        "& .MuiDialog-container": {
          boxSizing: "border-box",
          overflowY: "auto",
          padding: "clamp(12px, 4vw, 24px)",
        },
      }}
      BackdropProps={{ sx: { backgroundColor: "rgba(10, 10, 10, 0.45)" } }}
      PaperProps={{
        "aria-label": labelledBy ? undefined : ariaLabel || "Confirmation",
        sx: {
          alignItems: "center",
          backgroundColor: "#FFFFFF",
          border: "1px solid #E5E7EB",
          borderRadius: "14px",
          boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
          boxSizing: "border-box",
          color: "#0A0A0A",
          display: "flex",
          flexDirection: "column",
          fontFamily: "'DM Sans', sans-serif",
          margin: 0,
          maxHeight: "100%",
          maxWidth: "440px",
          padding: "30px 28px",
          position: "relative",
          textAlign: "center",
          width: "100%",
        },
      }}
    >
      <IconButton
        aria-label="Close"
        disabled={busy}
        onClick={handleClose}
        sx={{
          backgroundColor: "#F21B1B",
          color: "#FFFFFF",
          height: 28,
          padding: 0,
          position: "absolute",
          right: 14,
          top: 14,
          width: 28,
          "&:hover": { backgroundColor: "#D01414" },
        }}
      >
        <CloseIcon sx={{ fontSize: 13 }} />
      </IconButton>
      <div
        className="flex h-[64px] w-[64px] shrink-0 items-center justify-center rounded-full text-white [&_svg]:max-h-[58%] [&_svg]:max-w-[58%]"
        style={{ backgroundColor: circleColor }}
      >
        {icon}
      </div>
      {children}
    </Dialog>
  );
};

/** Design pop-up message text: 14px, max 340px, bold highlights at 600. */
export const designModalMessageStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.55,
  maxWidth: 340,
  textWrap: "pretty",
  width: "100%",
};

export default DesignModalShell;
